import { BlockProps, InlineFlex } from '@components/Layout/Blocks';
import { prop } from '@lib/style/theme';
import { between, classList } from '@lib/utils';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grow from '@material-ui/core/Grow';
import { useIntersect } from '@packages/hooks/useIntersect';
import { LazyImageError, LAZY_IMAGE_ERROR } from '@packages/image/LazyImage/LazyImageError';
import { ImageObject } from '@packages/image/lib/types';
import { Absolute } from '@packages/layout/lib/Blocks';
import { useLoading } from '@packages/loading';
import { createMemo } from '@packages/react';
import { applyRef } from '@packages/react/applyRef';
import { stripUnit } from 'polished';
import React, {
    CSSProperties,
    forwardRef,
    isValidElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';
import { getImage } from '../lib/getImage';

export type LazyImageProps = Partial<BlockProps> & {
    src: ImageObject | string;
    fit?: CSSProperties['objectFit'];
    alt?: string;
    ref?: any;
    fallback?: any;
    aspectRatio?: number;
    index?: number;
    thumbnail?: boolean;
    noMessage?: boolean;
    burstCache?: boolean;
    cursor?: CSSProperties['cursor'];
};

export const LazyImage = styled(
    createMemo(
        forwardRef<any, LazyImageProps>(
            (
                {
                    alt = 'image',
                    children,
                    fit,
                    src,
                    thumbnail,
                    fallback,
                    aspectRatio,
                    burstCache,
                    noMessage,
                    cursor,
                    ...rest
                },
                ref,
            ) => {
                const loading = useLoading(false);
                const [error, setError] = useState<LAZY_IMAGE_ERROR | null>(null);
                const intersect = useIntersect<HTMLImageElement>({ quickRelease: true });

                const source = useMemo<string | null>(
                    () =>
                        getImage(src, {
                            burstCache,
                            isThumbnail: !!thumbnail,
                        }),
                    [src, thumbnail],
                );

                const handleLoaded = useCallback(() => {
                    loading.stop();
                    if (intersect.element.current) {
                        intersect.element.current.style.opacity = '1';
                    }
                }, [intersect.element.current]);

                const handleError = useCallback(() => {
                    setError(LAZY_IMAGE_ERROR.NOT_FOUND);
                    handleLoaded();
                }, [setError]);

                useEffect(() => {
                    if (intersect.isIntersected) {
                        if (intersect.element.current && source) {
                            loading.start();
                            setError(null);
                            intersect.element.current.src = source;
                        } else {
                            setError(LAZY_IMAGE_ERROR.NO_IMAGE);
                        }
                    }
                }, [intersect.isIntersected, setError, source]);

                const aspectRationPercent = useMemo<number>(() => {
                    let percent = 1;

                    if (aspectRatio) {
                        if (aspectRatio === 1) {
                            percent = 1;
                        } else if (aspectRatio < 1) {
                            percent = percent + aspectRatio;
                        } else {
                            percent = aspectRatio - percent;
                        }
                    }

                    return percent * 100;
                }, [aspectRatio]);

                if (aspectRatio && !rest.padding) {
                    rest.padding = `0 0 ${aspectRationPercent}% 0`;
                }

                rest.className = classList('lazy-image', rest.className);
                const Fallback = fallback;
                const loaderRef = useRef<null | HTMLElement>(null);

                applyRef(rest, ref);

                const uWidth = parseInt(`${stripUnit(loaderRef.current?.clientWidth ?? rest.width ?? 24)}`);
                const loadingSize = between(Math.floor(uWidth / 4), 14, 30);

                return (
                    <InlineFlex align={'center'} justify={'center'} {...rest}>
                        <Absolute ref={loaderRef} t={0} l={0} r={0} w={'100%'} />
                        <Absolute
                            component={InlineFlex}
                            t={'50%'}
                            l={'50%'}
                            margin={`-${loadingSize / 2}px 0 0 -${loadingSize / 2}px`}
                        >
                            <Grow in={loading.state} unmountOnExit>
                                <CircularProgress size={loadingSize} color={'primary'} variant={'indeterminate'} />
                            </Grow>
                        </Absolute>
                        <Grow in={!error && !loading.state}>
                            <div className={'lazy-image__image--holder'}>
                                <img
                                    className={'lazy-image__image'}
                                    ref={intersect.element}
                                    alt={alt}
                                    onLoad={handleLoaded}
                                    onError={handleError}
                                />
                            </div>
                        </Grow>
                        <Grow in={!!error && !loading.state} unmountOnExit>
                            <InlineFlex className={'lazy-image__image--holder'} align={'center'} justify={'center'}>
                                {Fallback ? (
                                    isValidElement(Fallback) ? (
                                        Fallback
                                    ) : (
                                        <Fallback />
                                    )
                                ) : (
                                    <LazyImageError noMessage={noMessage} reason={error as any} />
                                )}
                            </InlineFlex>
                        </Grow>

                        {children}
                    </InlineFlex>
                );
            },
        ),
        {
            displayName: 'LazyImage',
        },
    ),
)`
    && {
        position: relative;
        overflow: hidden;
        .lazy-image {
            &__image {
                cursor: ${prop('cursor', 'default')};
                object-fit: ${prop('fit', 'contain')};
                transform: translate3d(0, 0, 0);
                opacity: 0;
                transition: opacity 200ms;
                &--holder {
                    overflow: hidden;
                }
                &,
                &--holder {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    left: 0;
                    right: 0;
                    top: 0;
                    bottom: 0;
                }
            }
        }
    }
`;

LazyImage.defaultProps = {
    width: '100%',
    height: '100%',
    thumbnail: true,
};
